import React, { useEffect, useMemo, useState } from 'react'
import {
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Autocomplete,
  TextField,
  CircularProgress,
} from '@mui/material'
import CurvaCrescimentoOMS from '../../consultorio/atendimentoMedico/components/CurvaCrescimentoOMS'
import {
  listaCurvaCrescimento,
  listaPacientesCurvaCrescimento,
  getCurvaCrescimento,
  listaCurvaCrescimentoPaciente,
} from './mockData'
import { setProgramaAberto } from '../../../components/api'

const DIAS_POR_MES = 30.4375

const getConfig = (indicadores, idCurva) => {
  const sel = indicadores?.find((c) => c.id_curva_crescimento === idCurva)
  if (!sel)
    return {
      campoPaciente: 'nr_peso',
      unidade: 'kg',
      titulo: 'Peso para Idade',
    }
  if (sel.ds_nome?.toLowerCase().includes('imc'))
    return {
      campoPaciente: 'nr_imc',
      unidade: 'kg/m²',
      titulo: 'IMC para Idade',
    }
  if (
    sel.ds_nome?.toLowerCase().includes('altura') ||
    sel.ds_nome?.toLowerCase().includes('estatura')
  )
    return {
      campoPaciente: 'nr_altura',
      unidade: 'cm',
      titulo: 'Altura para Idade',
    }
  if (
    sel.ds_nome?.toLowerCase().includes('cefálico') ||
    sel.ds_nome?.toLowerCase().includes('cafálica') ||
    sel.ds_nome?.toLowerCase().includes('circunferência')
  )
    return {
      campoPaciente: 'nr_perimetro_cefalico',
      unidade: 'cm',
      titulo: 'Perímetro Cefálico para Idade',
    }
  if (sel.ds_nome?.toLowerCase().includes('peso'))
    return {
      campoPaciente: 'nr_peso',
      unidade: 'kg',
      titulo: 'Peso para Idade',
    }
  return { campoPaciente: 'nr_peso', unidade: 'kg', titulo: sel.ds_nome }
}

const calcularIdade = (dtNascimento, dataReferencia = new Date()) => {
  if (!dtNascimento) return ''
  const nasc = parseData(dtNascimento)
  if (!nasc) return ''
  const ref = new Date(dataReferencia)
  let meses =
    (ref.getFullYear() - nasc.getFullYear()) * 12 +
    (ref.getMonth() - nasc.getMonth())
  if (ref.getDate() < nasc.getDate()) meses -= 1
  if (meses < 0) meses = 0
  if (meses < 24) return `${meses} ${meses === 1 ? 'mês' : 'meses'}`
  const anos = Math.floor(meses / 12)
  const resto = meses % 12
  if (resto === 0) return `${anos} ${anos === 1 ? 'ano' : 'anos'}`
  return `${anos}a ${resto}m`
}

const parseData = (dt) => {
  if (!dt) return null
  if (typeof dt === 'string' && dt.includes('/')) {
    const [dia, mes, anoHora] = dt.split('/')
    const [ano] = anoHora.split(' ')
    const d = new Date(`${ano}-${mes}-${dia}T00:00:00`)
    return Number.isNaN(d.getTime()) ? null : d
  }
  const d = new Date(dt)
  return Number.isNaN(d.getTime()) ? null : d
}

const formatarData = (dt) => {
  const d = parseData(dt)
  return d ? d.toLocaleDateString('pt-BR') : ''
}

const calcularDias = (dtNascimento, dtAvaliacao) => {
  const nasc = parseData(dtNascimento)
  const aval = parseData(dtAvaliacao)
  if (!nasc || !aval) return null
  const diff = aval.getTime() - nasc.getTime()
  return Math.floor(diff / (1000 * 60 * 60 * 24))
}

const corClassificacao = (classe) => {
  if (classe.includes('Muito baixo') || classe.includes('Muito elevado'))
    return 'error'
  if (classe.includes('Baixo') || classe.includes('Elevado')) return 'warning'
  return 'success'
}

const classificar = (valor, ref, tpCurva) => {
  if (!ref) return '-'
  if (tpCurva === 'P') {
    if (valor < ref.nr_percentil_3) return 'Muito baixo (< P3)'
    if (valor < ref.nr_percentil_15) return 'Baixo (P3 a P15)'
    if (valor <= ref.nr_percentil_85) return 'Adequado (P15 a P85)'
    if (valor <= ref.nr_percentil_97) return 'Elevado (P85 a P97)'
    return 'Muito elevado (> P97)'
  }
  if (valor < ref.nr_desvio_menos_3) return 'Muito baixo (< -3 DP)'
  if (valor < ref.nr_desvio_menos_2) return 'Baixo (-3 a -2 DP)'
  if (valor <= ref.nr_desvio_mais_2) return 'Adequado (-2 a +2 DP)'
  if (valor <= ref.nr_desvio_mais_3) return 'Elevado (+2 a +3 DP)'
  return 'Muito elevado (> +3 DP)'
}

let timeoutPaciente = null

const TesteCurvaCrescimento = () => {
  const [paciente, setPaciente] = useState(null)
  const [idCurva, setIdCurva] = useState(1)
  const [tpCurva, setTpCurva] = useState('Z')

  const [indicadores, setIndicadores] = useState([])
  const [pacientes, setPacientes] = useState([])
  const [buscandoPacientes, setBuscandoPacientes] = useState(false)
  const [valores, setValores] = useState([])
  const [medicoes, setMedicoes] = useState([])
  const [carregando, setCarregando] = useState(false)

  const config = useMemo(
    () => getConfig(indicadores, idCurva),
    [indicadores, idCurva],
  )

  useEffect(() => {
    setProgramaAberto('CtrlAtendimentoMedico')
    return () => {
      setProgramaAberto('')
    }
  }, [])

  useEffect(() => {
    let cancelado = false
    listaCurvaCrescimento().then((res) => {
      if (!cancelado && res.status) {
        setIndicadores(res.lista || [])
      }
    })
    return () => {
      cancelado = true
    }
  }, [])

  const buscarPacientes = (nmPaciente = '') => {
    if (timeoutPaciente) clearTimeout(timeoutPaciente)
    setBuscandoPacientes(true)
    timeoutPaciente = setTimeout(() => {
      listaPacientesCurvaCrescimento({ nmPaciente }).then((res) => {
        setPacientes(res.status ? res.lista || [] : [])
        setBuscandoPacientes(false)
      })
    }, 300)
  }

  useEffect(() => {
    buscarPacientes('')
    return () => {
      if (timeoutPaciente) clearTimeout(timeoutPaciente)
    }
  }, [])

  useEffect(() => {
    if (!paciente || !paciente.tp_sexo) {
      setValores([])
      setMedicoes([])
      return undefined
    }

    let cancelado = false
    const carregar = async () => {
      setCarregando(true)
      const [respValores, respMedicoes] = await Promise.all([
        getCurvaCrescimento({
          idCurvaCrescimento: idCurva,
          tpSexo: paciente.tp_sexo,
          tpCurva,
        }),
        listaCurvaCrescimentoPaciente({ idPaciente: paciente.id_paciente }),
      ])
      if (!cancelado) {
        setValores(respValores.status ? respValores.lista : [])
        setMedicoes(respMedicoes.status ? respMedicoes.lista : [])
        setCarregando(false)
      }
    }
    carregar()
    return () => {
      cancelado = true
    }
  }, [paciente, idCurva, tpCurva])

  const campoMediana = tpCurva === 'P' ? 'nr_percentil_50' : 'nr_mediana'
  const campoInf = tpCurva === 'P' ? 'nr_percentil_3' : 'nr_desvio_menos_2'
  const campoSup = tpCurva === 'P' ? 'nr_percentil_97' : 'nr_desvio_mais_2'

  const linhasTabela = useMemo(() => {
    return medicoes
      .map((p) => {
        const idadeDias =
          calcularDias(paciente?.dt_nascimento, p.dt_avaliacao) ??
          p.nr_idade_dias
        const idadeMeses = idadeDias / DIAS_POR_MES
        const mesProximo = Math.round(idadeMeses)
        const ref = valores.find((v) => v.nr_idade_meses === mesProximo)
        const valor = p[config.campoPaciente]
        const classe = classificar(valor, ref, tpCurva)
        return { p, idadeMeses, ref, valor, classe }
      })
      .filter((l) => l.valor != null)
  }, [medicoes, valores, config, tpCurva, paciente])

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Curva de Crescimento (OMS)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Valores de referência OMS (0-5 anos) comparados às medições do paciente.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={pacientes}
            value={paciente}
            onChange={(_, v) => setPaciente(v)}
            onInputChange={(_, v) => buscarPacientes(v)}
            loading={buscandoPacientes}
            getOptionLabel={(option) =>
              option?.nm_paciente
                ? `${option.nm_paciente} · ${calcularIdade(
                    option.dt_nascimento,
                  )}`
                : ''
            }
            isOptionEqualToValue={(option, val) =>
              option?.id_paciente === val?.id_paciente
            }
            renderOption={(props, option) => (
              <li {...props}>
                <Box>
                  <Typography variant="body2">{option.nm_paciente}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.tp_sexo === 'M'
                      ? 'Masculino'
                      : option.tp_sexo === 'F'
                        ? 'Feminino'
                        : 'Sexo não informado'}{' '}
                    · {calcularIdade(option.dt_nascimento)} ·{' '}
                    {formatarData(option.dt_nascimento)}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Paciente"
                size="small"
                margin="dense"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {buscandoPacientes ? (
                        <CircularProgress color="inherit" size={14} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth margin="dense">
            <InputLabel>Indicador</InputLabel>
            <Select
              value={idCurva}
              label="Indicador"
              size="small"
              onChange={(e) => setIdCurva(e.target.value)}
            >
              {indicadores.map((c) => (
                <MenuItem
                  key={c.id_curva_crescimento}
                  value={c.id_curva_crescimento}
                >
                  {c.ds_nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <ToggleButtonGroup
            value={tpCurva}
            exclusive
            onChange={(e, v) => v && setTpCurva(v)}
            size="small"
            sx={{ mt: 1 }}
          >
            <ToggleButton value="Z">Desvios (Z)</ToggleButton>
            <ToggleButton value="P">Percentis (P)</ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>

      {paciente ? (
        <>
          {!paciente.tp_sexo ? (
            <Paper sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
              O paciente selecionado não possui sexo informado. A curva de
              crescimento não pode ser exibida sem essa informação.
            </Paper>
          ) : carregando ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Paper sx={{ p: 3, mb: 4 }}>
                <CurvaCrescimentoOMS
                  valores={valores}
                  paciente={medicoes}
                  campoPaciente={config.campoPaciente}
                  tpCurva={tpCurva}
                  titulo={`${config.titulo} - ${
                    paciente.tp_sexo === 'M' ? 'Menino' : 'Menina'
                  }`}
                  unidade={config.unidade}
                  dtNascimento={paciente.dt_nascimento}
                />
              </Paper>

              <Typography variant="h6" gutterBottom>
                Valores do paciente x Referência OMS
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell align="right">Idade (meses)</TableCell>
                      <TableCell align="right">
                        Paciente ({config.unidade})
                      </TableCell>
                      <TableCell align="right">
                        {tpCurva === 'P' ? 'P50' : 'Mediana'}
                      </TableCell>
                      <TableCell align="right">
                        {tpCurva === 'P' ? 'P3' : '-2 DP'}
                      </TableCell>
                      <TableCell align="right">
                        {tpCurva === 'P' ? 'P97' : '+2 DP'}
                      </TableCell>
                      <TableCell>Classificação</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {linhasTabela.map(
                      ({ p, idadeMeses, ref, valor, classe }) => (
                        <TableRow key={p.id_curva_crescimento_paciente}>
                          <TableCell>{formatarData(p.dt_avaliacao)}</TableCell>
                          <TableCell align="right">
                            {idadeMeses.toFixed(1)}
                          </TableCell>
                          <TableCell align="right">
                            <strong>{valor}</strong>
                          </TableCell>
                          <TableCell align="right">
                            {ref ? ref[campoMediana] : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {ref ? ref[campoInf] : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {ref ? ref[campoSup] : '-'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={classe}
                              color={corClassificacao(classe)}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      ) : (
        <Paper sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
          Selecione um paciente para visualizar a curva de crescimento.
        </Paper>
      )}
    </Box>
  )
}

export default TesteCurvaCrescimento
